"use client"

import * as React from 'react';
import {useState} from 'react';
import {capitalize} from "../../utils/StringUtilities";
import FontAwesome from "../utilities/FontAwesome";
import {observer} from "mobx-react";
import {YiiUrlFactory} from "../../backends/YiiRoutes";
import {sb_hideAllAdminNavModals} from "../../utils/SchoolBlocks";
import NotificationManager from "../notifications/NotificationManager";
import {useGridType} from "../../hooks/useGridType";
import {GridTypes} from "../../pages/MainGridPage";
import {IView} from "../../stores/InterfaceStore";
import {useCreateContentItem} from "../../hooks/useCreateContentItem";
import {isSchoolFeedStandalone} from "../../utils/SchoolBlocksUtilities";
import ImageComponent from "../utilities/ImageComponent";
import {useStores} from "../../stores/useStores";

export const useAdminMenuFields = () => {
    const {organizationStore, interfaceStore, i18nStore} = useStores();

    const isSchoolFeedApp = isSchoolFeedStandalone();
    let pageType = organizationStore.currentOrganization.type;
    let manageText, deleteText, deleteUrl, addText, showFluxCapacitor, showGridSettings = false;

    const gridType = useGridType();

    const createTexts: Array<{ text: string, pageType: string }> = [];

    if (organizationStore.currentOrganization.type === 'collection') {
        const rules = organizationStore.currentOrganization.collectionRules;
        rules.forEach(rule => {
            if (rule.collection_of === 'organizations' && rule.type === 'type') {
                rule.rule.forEach(type => {
                    if (!["school", "district"].includes(type)) {
                        createTexts.push({
                            text: "Create " + capitalize(type),
                            pageType: type,
                        })
                    }
                })
            } else if (rule.collection_of === "people") {
                pageType = "peopleCollection";
            }
            else if (rule.type==='category') {
                createTexts.push({
                    text: "Create Organization",
                    pageType: "organization",
                })
            }
        })
    }

    if (interfaceStore.view === IView.CALENDAR || interfaceStore.view === IView.MYCALENDAR) {
        pageType = "calendar";
    }
    if (interfaceStore.view === IView.SCHOOLFEED_PAGE || (interfaceStore.view === IView.DEFAULT && isSchoolFeedApp)) {
        pageType = "schoolfeed";
    }

    const addType: "content" | "blocks" = pageType === "schoolfeed" || gridType === GridTypes.terraced ? "content" : "blocks";

    switch (pageType) {
        case "peopleCollection":
        case "collection":
            if (isSchoolFeedApp){
                showGridSettings = true;
            }
            else {
                manageText = 'Manage Members';
            }
            break;
        case "page":
            showGridSettings = true;
            deleteText = 'Delete Page';
            deleteUrl = YiiUrlFactory(`/cms/page/delete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            break;
        case "group":
        case "department":
        case "section":
        case "team":
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            deleteText = 'Delete ' + capitalize(organizationStore.currentOrganization.type);
            deleteUrl = YiiUrlFactory(`/cms/section/softDelete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            showFluxCapacitor = true;
            showGridSettings = true;
            break;
        case "classroom":
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            deleteText = 'Delete Classroom';
            deleteUrl = YiiUrlFactory(`/cms/classroom/softDelete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            showFluxCapacitor = true;
            showGridSettings = true;
            break;
        case "calendar":
            manageText = 'Manage Calendars';
            break;
        case "sharedfiles":
            deleteText = `Delete Page`;
            break;
        case "schoolfeed":
            if (organizationStore.currentOrganization.type !== "collection") {
                addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            }
            showGridSettings = isSchoolFeedApp;
            break;
        default:
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            showFluxCapacitor = true;
            showGridSettings = true;
            break;
    }

    if (gridType === GridTypes.terraced || isSchoolFeedApp) {
        // vertical grid doesn't support grid states so flux should be disabled
        showFluxCapacitor = false;
    }

    return {
        pageType,
        addText,
        showFluxCapacitor,
        manageText,
        deleteText,
        createTexts,
        deleteUrl,
        gridType,
        showGridSettings,
        addType,
    }
}

const AdminMenu: React.FC<{}> = observer(props => {
    const {
        sidebarStore,
        organizationStore,
        userStore,
        modalStore,
        gridStore,
        interfaceStore,
    } = useStores();
    const [activeModal, setActiveModal] = useState<string | null>(null);

    const {
        pageType,
        addText,
        showFluxCapacitor,
        manageText,
        deleteText,
        createTexts,
        deleteUrl,
        gridType,
        showGridSettings,
        addType,
    } = useAdminMenuFields();

    const {handleSave} = useCreateContentItem()

    function hideAllModalsExcept(except: string, modalObj?: any) {
        modalStore.clearAllModals();
        sb_hideAllAdminNavModals(except);
        if (modalObj && activeModal !== modalObj.type) {
            modalStore.addModal(modalObj);
        }
        setActiveModal(null);
    }

    return <div className="sb-admin-nav">
        <nav className="sb-admin-main-nav" aria-label="Administrative Menu">
            <ul>
                {showGridSettings && userStore.isAdmin && <li key="gridsettings">
                    <button className={`btn-icon`}
                            onClick={() => {
                                sidebarStore.setSidebar({
                                    view: "pagesettings",
                                })
                            }}
                            title={"Page Settings"}>
                        <FontAwesome name="fas fa-cog" aria-hidden={true}/>
                    </button>
                </li>}
                {pageType === 'sharedfiles' && userStore.isAdmin && <li key="publishdelete">
                    <button className={`btn-icon`}
                            onClick={() => {
                                sidebarStore.setSidebar({
                                    view: "PublishDeletePage",
                                })
                            }}
                            title={"Page Settings"}>
                        <FontAwesome name="fas fa-cog" aria-hidden={true}/>
                    </button>
                </li>}
                {manageText && userStore.isAdmin && <li key="manage">
                    <button className={`sb-admin-menu-manage-${pageType} btn-icon`}
                            onClick={() => {
                                if (organizationStore.currentOrganization.type === "collection") {
                                    hideAllModalsExcept("", {
                                        id: pageType === "peopleCollection" ? 'people-collection-management-modal' : 'collection-management-modal',
                                        type: pageType === "peopleCollection" ? 'peopleCollectionManagement' : 'collectionManagement',
                                        title: 'Manage Members',
                                        itemObj: {},
                                        gridId: gridStore.id,
                                    })
                                } else if (pageType === "calendar") {
                                    hideAllModalsExcept("", {
                                        type: "calendarManagement",
                                    })
                                }
                            }}
                            title={manageText}>
                        <FontAwesome name="fas fa-cog" aria-label={manageText}/>
                    </button>
                </li>}
                {createTexts.length > 0 && <li key={"Add Page"}>
                    <button className={"btn-icon"}
                            title={"Add Page"}
                            aria-label={"Add Page"}
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "addOrEditBlock",
                                    blockType: createTexts[0].pageType,
                                    intent: "add",
                                    createTexts: createTexts,
                                });
                            }}>
                        <FontAwesome name="fas fa-plus-circle" aria-label={"Add Page"}/>
                    </button>
                </li>}
                {addText && (addType === "content" ? <li key="add">
                    <button id="btn-trigger-add-block" className={"btn-icon"}
                            title="Add content"
                            aria-label="Add content"
                            onClick={() => {
                                sidebarStore.setSidebar({
                                    view: "SchoolFeedPost",
                                    handleSave: handleSave,
                                })
                            }}>
                        {addText}
                    </button>
                </li> : <li key="add" className={activeModal === "toolDrawerGrid" ? "active" : ""}>
                    <button id="btn-trigger-add-block" className={"btn-icon"}
                            title="Add content"
                            aria-label="Add content"
                            onClick={function () {
                                if (gridStore.blocks.size > 11) {
                                    NotificationManager.warning(
                                        "Can we help you improve page layout with fewer blocks? Contact support" +
                                        " to discuss further!",
                                        "Hmmm, that's a lot of blocks.",
                                        5000,
                                    )
                                }
                                hideAllModalsExcept("", {
                                    type: "toolDrawerGrid",
                                    setActiveModal,
                                })
                            }}>
                        {addText}
                    </button>
                    <span className="curvetop" role="presentation" aria-hidden="true"/>
                    <span className="curvebottom" role="presentation" aria-hidden="true"/>
                </li>)}
                {pageType === 'page' && <>
                    <li key="editPage">
                        <button className={"btn-icon"}
                                title="Edit page"
                                aria-label="Edit page"
                                onClick={() => {
                                    hideAllModalsExcept("editPage");
                                    interfaceStore.toggleEditingPage();
                                }}>
                            <FontAwesome name="fas fa-pencil-alt" aria-label="Edit page"/>
                        </button>
                    </li>
                    <li key="compareVersions">
                        <button className={"btn-icon"}
                                title="Compare versions"
                                aria-label="Compare versions"
                                onClick={() => {
                                    hideAllModalsExcept("", {
                                        id: "comparePagesModal",
                                        type: "comparePages",
                                    })
                                }}>
                            <FontAwesome name="fas fa-undo" aria-label="compare versions"/>
                        </button>
                    </li>
                </>}
                {showFluxCapacitor && <li key="flux">
                    <button title="Flux Capacitor" className={"btn-icon"}
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "fluxCapacitor",
                                });
                            }}
                            aria-label="Open Flux Capacitor">
                        <ImageComponent alt="Open Flux Capacitor" src={require("../../assets/images/flux-capacitor.svg") as string}
                             width="32px"/>
                    </button>
                </li>}
                <li key="help" className={activeModal === "helpModal" ? "active" : ""}>
                    <button id="btn-trigger-help-window" role="button" className={"btn-icon"}
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "helpModal",
                                    setActiveModal,
                                });
                            }}
                            aria-label="Need help?"
                            title="Need help?">
                        <FontAwesome prefix={"fas"} name="fa-question-circle" aria-label="Need help?"/>
                    </button>
                    <span className="curvetop" role="presentation" aria-hidden="true"/>
                    <span className="curvebottom" role="presentation" aria-hidden="true"/>
                </li>
            </ul>
        </nav>
    </div>
});

export default AdminMenu;
